import { Badge, Flex, Text } from "@mantine/core";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import ActionIcon from "../../../../components/ActionIcon/ActionIcon.tsx";
import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgEdit from "../../../../components/Icons/Edit.tsx";
import Spoiler from "../../../../components/Spoiler/Spoiler.tsx";
import TableFilesDisplay from "../../../../components/TableFilesDisplay/TableFilesDisplay.tsx";
import {
  CreditApproval,
  toHumanReadableCAResult,
} from "../../../../types/creditApproval.ts";
import { LimitRequestConditionPrecedent } from "../../../../types/limitRequestConditionPrecedent.ts";
import { LimitRequestException } from "../../../../types/limitRequestException.ts";
import { toLocalDate } from "../../../../utils/date.ts";
import { toCurrencyPrice, toPercent } from "../../../../utils/number.ts";
import { toHumanReadable } from "../../../../utils/string.ts";
import { useStyles } from "../CreditCommitteeCard.styles.ts";

interface Props {
  creditCommittee: Partial<CreditApproval> | null | undefined;
  exceptions: LimitRequestException[];
  conditionPrecedents: LimitRequestConditionPrecedent[];
  hasEditAccess: boolean;
  onEditConditionPrecedents: () => void;
  onEditExceptions: () => void;
}

export const useCreditCommitteeCardData = ({
  creditCommittee,
  exceptions,
  conditionPrecedents,
  hasEditAccess,
  onEditExceptions,
  onEditConditionPrecedents,
}: Props) => {
  const { classes } = useStyles();

  const [showAllConditionPrecedents, setShowAllConditionPrecedents] =
    useState(false);
  const [showAllExceptions, setShowAllExceptions] = useState(false);

  const generateConditionPrecedentCardItem = useCallback(
    (item: LimitRequestConditionPrecedent) => {
      return [
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestConditionPrecedent",
                fieldName: "name",
              }}
              position="right"
            >
              Name
            </DatabaseMetadataPopover>
          ),
          content: item.name,
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestConditionPrecedent",
                fieldName: "completed",
              }}
              position="right"
            >
              Status
            </DatabaseMetadataPopover>
          ),
          content: (
            <Badge
              variant={item.completed ? "active" : "inactive"}
              className={classes.badge}
            >
              {item.completed ? "Complete" : "Not Complete"}
            </Badge>
          ),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestConditionPrecedent",
                fieldName: "type",
              }}
              position="right"
            >
              Type
            </DatabaseMetadataPopover>
          ),
          content: toHumanReadable(item.type),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestConditionPrecedent",
                fieldName: "dueDate",
              }}
              position="right"
            >
              Due date
            </DatabaseMetadataPopover>
          ),
          content: toLocalDate(item.dueDate),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestConditionPrecedent",
                fieldName: "documents",
              }}
              position="right"
            >
              Document Submission
            </DatabaseMetadataPopover>
          ),
          content:
            item.documents &&
            (item.documents.length > 1 ? (
              <TableFilesDisplay files={item.documents} />
            ) : (
              <Link to={item.documents[0].url} target="_blank">
                Download
              </Link>
            )),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestConditionPrecedent",
                fieldName: "notes",
              }}
              position="right"
            >
              Notes
            </DatabaseMetadataPopover>
          ),
          content: item.notes,
        },
      ];
    },
    [classes.badge]
  );

  const generateExceptionCardItem = useCallback(
    (item: LimitRequestException) => {
      return [
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestException",
                fieldName: "name",
              }}
              position="right"
            >
              Name
            </DatabaseMetadataPopover>
          ),
          content: item.name,
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestException",
                fieldName: "completed",
              }}
              position="right"
            >
              Status
            </DatabaseMetadataPopover>
          ),
          content: (
            <Badge
              variant={item.completed ? "active" : "inactive"}
              className={classes.badge}
            >
              {item.completed ? "Complete" : "Not Complete"}
            </Badge>
          ),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestException",
                fieldName: "documents",
              }}
              position="right"
            >
              Document Submission
            </DatabaseMetadataPopover>
          ),
          content:
            item.documents &&
            (item.documents.length > 1 ? (
              <TableFilesDisplay files={item.documents} />
            ) : (
              <Link to={item.documents[0].url} target="_blank">
                Download
              </Link>
            )),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "LimitRequestException",
                fieldName: "notes",
              }}
              position="right"
            >
              Notes
            </DatabaseMetadataPopover>
          ),
          content: item.notes,
        },
      ];
    },
    [classes.badge]
  );

  return useMemo(() => {
    return [
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "limitName",
            }}
            position="right"
          >
            Limit Name
          </DatabaseMetadataPopover>
        ),
        content: creditCommittee?.limitName,
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "date",
            }}
            position="right"
          >
            CC date
          </DatabaseMetadataPopover>
        ),
        content: toLocalDate(creditCommittee?.date),
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "limitRecommendation",
            }}
            position="right"
          >
            Limit approved
          </DatabaseMetadataPopover>
        ),
        content: toCurrencyPrice(creditCommittee?.limitRecommendation),
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "tenorRecommendation",
            }}
            position="right"
          >
            Tenor approved
          </DatabaseMetadataPopover>
        ),
        content: creditCommittee?.tenorRecommendation,
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "pricingRecommendation",
            }}
            position="right"
          >
            Pricing approved
          </DatabaseMetadataPopover>
        ),
        content: toPercent(
          creditCommittee?.pricingRecommendation,
          "percentage"
        ),
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "insuranceLimitRecommendation",
            }}
            position="right"
          >
            Insurance limit approved
          </DatabaseMetadataPopover>
        ),
        content: toCurrencyPrice(creditCommittee?.insuranceLimitRecommendation),
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "LimitRequest",
              fieldName: "conditionPrecedents",
            }}
            position="right"
          >
            Condition precedence
          </DatabaseMetadataPopover>
        ),
        content: (
          <Flex className={classes.expand} gap={5}>
            <Text
              onClick={() =>
                setShowAllConditionPrecedents(!showAllConditionPrecedents)
              }
            >
              {showAllConditionPrecedents
                ? `Hide (${conditionPrecedents?.length})`
                : `Show All (${conditionPrecedents?.length})`}
            </Text>
            {hasEditAccess && (
              <ActionIcon
                size={25}
                spaces={false}
                onClick={onEditConditionPrecedents}
              >
                <SvgEdit />
              </ActionIcon>
            )}
          </Flex>
        ),
        openNestedContent: showAllConditionPrecedents,
        nestedContent: conditionPrecedents
          ? conditionPrecedents.map(generateConditionPrecedentCardItem)
          : undefined,
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "LimitRequest",
              fieldName: "exceptions",
            }}
            position="right"
          >
            Exception
          </DatabaseMetadataPopover>
        ),
        content: (
          <Flex className={classes.expand} gap={5}>
            <Text onClick={() => setShowAllExceptions(!showAllExceptions)}>
              {showAllExceptions
                ? `Hide (${exceptions?.length})`
                : `Show All (${exceptions?.length})`}
            </Text>
            {hasEditAccess && (
              <ActionIcon size={25} spaces={false} onClick={onEditExceptions}>
                <SvgEdit />
              </ActionIcon>
            )}
          </Flex>
        ),
        openNestedContent: showAllExceptions,
        nestedContent: exceptions
          ? exceptions.map(generateExceptionCardItem)
          : undefined,
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "result",
            }}
            position="right"
          >
            Result
          </DatabaseMetadataPopover>
        ),
        content: toHumanReadableCAResult(creditCommittee?.result),
      },
      {
        label: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "CreditApproval",
              fieldName: "notes",
            }}
            position="right"
          >
            Notes
          </DatabaseMetadataPopover>
        ),
        content: creditCommittee?.notes && (
          <Spoiler maxStringLength={50} text={creditCommittee.notes} />
        ),
      },
    ];
  }, [
    creditCommittee,
    classes.expand,
    showAllConditionPrecedents,
    conditionPrecedents,
    hasEditAccess,
    onEditConditionPrecedents,
    generateConditionPrecedentCardItem,
    showAllExceptions,
    exceptions,
    onEditExceptions,
    generateExceptionCardItem,
  ]);
};
